// ✅ src/pages/OrderManagement/PhotoDocument.jsx
// FULL FILE — header-aware version with localStorage headers
//
// Backend mount:
//   app.use("/api/projectPhotos", projectPhotos);
//
// Endpoints used:
//   GET  https://nissi-india.com/api/projectPhotos/folders?contractId=1&branchId=8&branchType=HO
//   POST https://nissi-india.com/api/projectPhotos/folders/create-next
//
// Click folder:
//   /OrderManagement/AddPhotoDocument?contractId=1&folderId=123

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaFileInvoiceDollar,
  FaCalendarAlt,
  FaBuilding,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBars,
  FaHome,
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaTasks,
  FaWarehouse,
  FaUpload,
  FaFileImage,
  FaTrash,
  FaCamera,
  FaFolder,
  FaPlus,
  FaBriefcase,
  FaSyncAlt,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/projectPhotos";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toNum = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const PhotoDocumentComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState("Photos");

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid");

  // ✅ parsed values
  const userId = useMemo(() => toInt(userIdLS || 0), [userIdLS]);
  const branchId = useMemo(() => toInt(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchTypeLS || "").trim(), [branchTypeLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branchtype": branchTypeLS,
      "x-userid": userIdLS,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userIdLS]
  );

  // ✅ contractId from URL or sessionStorage
  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    return toNum(fromUrl || fromSession || 0);
  }, [location.search]);

  // ✅ keep contractId in sessionStorage
  useEffect(() => {
    if (contractId > 0) {
      sessionStorage.setItem("Selcontractid", String(contractId));
    }
  }, [contractId]);

  const withContractId = (path) => {
    if (!contractId) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("contractId", contractId);
    return url.pathname + url.search;
  };

  const sidebarItems = useMemo(
    () => [
      { name: "Date/LOA", icon: FaCalendarAlt, path: withContractId("/OrderManagement/ProjectEntry") },
      { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: withContractId("/OrderManagement/BOQ") },
      { name: "Program/DPR", icon: FaChartBar, path: withContractId("/OrderManagement/DailyProgressMonthlyWise") },
      { name: "Purchase Order", icon: FaFileAlt, path: withContractId("/OrderManagement/PurchaseOrderListing") },
      { name: "Rate Approval", icon: FaCheckCircle, path: withContractId("/OrderManagement/RateApprove") },
      { name: "Risk/Issues", icon: FaExclamationTriangle, path: withContractId("/OrderManagement/RiskAndIssue") },
      { name: "Documentation", icon: FaFileAlt, path: withContractId("/OrderManagement/ProjectEntryDocumentation") },
      { name: "Photos", icon: FaBuilding, path: withContractId("/OrderManagement/PhotoDocumentation") },
      { name: "Reconciliation", icon: FaTasks, path: withContractId("/OrderManagement/Reconciliation") },
      { name: "Do/Don't's", icon: FaWarehouse, path: withContractId("/OrderManagement/Rules") },
      { name: "Vendor Details", icon: FaUsers, path: withContractId("/OrderManagement/ProjectVendorDetails") },
    ],
    [contractId]
  );

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem.name);
  };

  // ✅ folder data
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderErr, setFolderErr] = useState("");
  const [inProgressFolders, setInProgressFolders] = useState([]);
  const [briefcaseFolder, setBriefcaseFolder] = useState(null);

  const loadFolders = async () => {
    if (!contractId) {
      setInProgressFolders([]);
      setBriefcaseFolder(null);
      setFolderErr("contractId missing in URL");
      return;
    }

    setLoadingFolders(true);
    setFolderErr("");

    try {
      const res = await axios.get(`${API_BASE}/folders`, {
        params: {
          contractId,
          branchId,
          branchType,
          userId,
        },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setInProgressFolders([]);
        setBriefcaseFolder(null);
        setFolderErr(res.data?.message || "Failed to load folders");
        return;
      }

      setInProgressFolders(Array.isArray(res.data?.foldersF) ? res.data.foldersF : []);
      setBriefcaseFolder(res.data?.folderB || null);
    } catch (e) {
      console.error("Folders load error:", e);
      setInProgressFolders([]);
      setBriefcaseFolder(null);
      setFolderErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoadingFolders(false);
    }
  };

  useEffect(() => {
    const moduleSelect =
      sessionStorage.getItem("ModuleSelect") ||
      sessionStorage.getItem("moduleSelect") ||
      "ERP";

    if (String(moduleSelect).toUpperCase() !== "ERP") {
      window.location.href = "/ModuleHomePage";
      return;
    }

    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, branchId, branchType, userId, commonHeaders]);

  const createNextFolder = async () => {
    if (!contractId) {
      alert("contractId missing");
      return;
    }

    setLoadingFolders(true);
    setFolderErr("");

    try {
      const res = await axios.post(
        `${API_BASE}/folders/create-next`,
        {
          contractId,
          branchId,
          branchType,
          userId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...commonHeaders,
          },
        }
      );

      if (!res.data?.success) {
        setFolderErr(res.data?.message || "Create folder failed");
        return;
      }

      await loadFolders();
    } catch (e) {
      console.error("Create folder error:", e);
      setFolderErr(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoadingFolders(false);
    }
  };

  const openFolder = (folderId) => {
    if (!folderId) return;
    navigate(`/OrderManagement/AddPhotoDocument?contractId=${contractId}&folderId=${folderId}`);
  };

  // ======= SIMULATION BLOCK =======
  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: 1,
      name: "project_progress_01.jpg",
      type: "image",
      size: "2.5 MB",
      status: "uploading",
      progress: 75,
      icon: "📁",
    },
    {
      id: 2,
      name: "completed_work.pdf",
      type: "document",
      size: "1.8 MB",
      status: "completed",
      progress: 100,
      icon: "📋",
    },
  ]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    files.forEach((file) => {
      const newFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        type: file.type.startsWith("image/") ? "image" : "document",
        size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
        status: "uploading",
        progress: 0,
        icon: file.type.startsWith("image/") ? "📁" : "📋",
      };

      setUploadedFiles((prev) => [...prev, newFile]);

      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;

        if (progress <= 100) {
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === newFile.id ? { ...f, progress } : f))
          );
        }

        if (progress >= 100) {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === newFile.id ? { ...f, status: "completed", progress: 100 } : f
            )
          );
          clearInterval(interval);
        }
      }, 200);
    });
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const ProgressBar = ({ progress, status }) => (
    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${
          status === "completed" ? "bg-green-500" : "bg-blue-500"
        }`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );

  const FileItem = ({ file }) => (
    <div
      className={`p-4 border rounded-lg transition-all duration-200 hover:shadow-md ${
        isDark ? "bg-gray-700 border-gray-600" : "bg-white border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="text-3xl">{file.icon}</div>
          <div className="flex-1">
            <h4 className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
              {file.name}
            </h4>
            <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {file.size} • {file.status === "completed" ? "Completed" : "In Progress"}
            </p>
            <ProgressBar progress={file.progress} status={file.status} />
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {file.progress}% {file.status === "completed" ? "Complete" : "Uploading..."}
            </p>
          </div>
        </div>
        <button
          onClick={() => removeFile(file.id)}
          className="text-red-500 hover:text-red-700 transition-colors"
          type="button"
        >
          <FaTrash size={14} />
        </button>
      </div>
    </div>
  );

  const FolderTile = ({ label, icon: Icon, onClick, variant = "normal" }) => (
    <button
      onClick={onClick}
      className={`p-5 rounded-2xl shadow-xl border transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] text-left ${
        isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
      }`}
      type="button"
      title={label}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className={`text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-500"}`}>
            {variant === "briefcase" ? "COMPLETED" : variant === "add" ? "CREATE" : "INPROGRESS"}
          </div>
          <div className={`mt-1 text-lg font-bold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
            {label}
          </div>
          <div className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Click to open
          </div>
        </div>

        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
            variant === "add"
              ? isDark
                ? "bg-emerald-700/40"
                : "bg-emerald-100"
              : variant === "briefcase"
              ? isDark
                ? "bg-indigo-700/40"
                : "bg-indigo-100"
              : isDark
              ? "bg-blue-700/40"
              : "bg-blue-100"
          }`}
        >
          <Icon
            className={`text-2xl ${
              variant === "add"
                ? isDark
                  ? "text-emerald-200"
                  : "text-emerald-700"
                : variant === "briefcase"
                ? isDark
                  ? "text-indigo-200"
                  : "text-indigo-700"
                : isDark
                ? "text-blue-200"
                : "text-blue-700"
            }`}
          />
        </div>
      </div>
    </button>
  );

  const isActivePath = (path) => {
    const pathname = window.location.pathname;
    const targetPath = new URL(path, window.location.origin).pathname;
    return pathname === targetPath || pathname.startsWith(targetPath);
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      {/* Top Bar */}
      <div
        className={`sticky top-0 z-50 backdrop-blur-md border-b ${
          isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } shadow-lg`}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"
                }`}
                type="button"
              >
                <FaBars size={20} />
              </button>

              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                disabled={loadingFolders}
                onClick={loadFolders}
                type="button"
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                  loadingFolders
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                onClick={() => setIsDark(!isDark)}
                type="button"
                className={`p-2 rounded-lg transition-colors ${
                  isDark
                    ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                    : "bg-gray-800 hover:bg-gray-900 text-white"
                }`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div
          className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          } border-r min-h-screen`}
        >
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={item.path}
                key={index}
                onClick={() => handleMenuItemClick(item)}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  isActivePath(item.path)
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 min-w-0">
          <div
            className={`transition-all duration-300 min-h-screen ${
              isDark
                ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900"
                : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
            }`}
          >
            {/* Header */}
            <div
              className={`backdrop-blur-md border rounded-2xl m-6 mb-6 ${
                isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"
              }`}
            >
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1
                      className={`text-2xl lg:text-3xl font-bold flex items-center gap-3 ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <FaCamera className="text-blue-500" />
                      PHOTO / DOCUMENT
                    </h1>

                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Monthly folders (InProgress) + Briefcase (Completed)
                    </p>

                    <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      ContractId: {contractId || "-"} | BranchType: {branchType || "-"} | BranchId: {branchId || "-"} |
                      UserId: {userId || "-"} | Active Menu: {activeMenuItem}
                    </p>

                    {folderErr ? (
                      <p className={`text-xs mt-1 ${isDark ? "text-red-300" : "text-red-600"}`}>
                        {folderErr}
                      </p>
                    ) : null}

                    {loadingFolders ? (
                      <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </p>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      disabled={loadingFolders}
                      onClick={createNextFolder}
                      type="button"
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loadingFolders
                          ? "bg-emerald-400 text-white cursor-not-allowed"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white"
                      }`}
                    >
                      <FaPlus />
                      Add Month Folder
                    </button>

                    <label className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 cursor-pointer">
                      <FaUpload />
                      Upload Files
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>

                    <button
                      type="button"
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaDownload />
                      Download All
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 space-y-6">
              {/* Folders */}
              <div
                className={`rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">FOLDERS</h2>
                </div>

                <div className="p-6 space-y-6">
                  {folderErr ? (
                    <div
                      className={`p-4 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-200"
                          : "bg-red-50 border-red-200 text-red-700"
                      }`}
                    >
                      {folderErr}
                    </div>
                  ) : null}

                  {/* InProgress */}
                  <div>
                    <div className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      <span className="text-red-500">InProgress</span>
                      {loadingFolders ? (
                        <span className={`ml-2 text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                          (Loading...)
                        </span>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {inProgressFolders.map((f, idx) => (
                        <FolderTile
                          key={f.folderId || idx}
                          label={f.folderName || `Folder ${idx + 1}`}
                          icon={FaFolder}
                          onClick={() => openFolder(f.folderId)}
                        />
                      ))}

                      <FolderTile
                        label="Add"
                        icon={FaPlus}
                        variant="add"
                        onClick={createNextFolder}
                      />
                    </div>
                  </div>

                  {/* Completed */}
                  <div>
                    <div className={`text-sm font-bold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
                      <span className="text-red-500">Completed</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {briefcaseFolder ? (
                        <FolderTile
                          label={briefcaseFolder.folderName || "BRIEF CASE"}
                          icon={FaBriefcase}
                          variant="briefcase"
                          onClick={() => openFolder(briefcaseFolder.folderId)}
                        />
                      ) : (
                        <div
                          className={`p-4 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-200"
                              : "bg-gray-50 border-gray-200 text-gray-600"
                          }`}
                        >
                          Briefcase folder not found.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload Center */}
              <div
                className={`rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">FILE UPLOAD CENTER (Simulation)</h2>
                </div>

                <div className="p-6">
                  <label
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 hover:border-blue-500 block ${
                      isDark ? "border-gray-600 hover:bg-gray-700/30" : "border-gray-300 hover:bg-blue-50"
                    }`}
                  >
                    <FaUpload
                      className={`mx-auto text-4xl mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    />
                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                      Drop files here or click to upload
                    </h3>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Support for images (JPG, PNG, GIF) and documents (PDF, DOC, DOCX)
                    </p>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Uploaded files */}
              <div
                className={`rounded-2xl shadow-xl border ${
                  isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"
                }`}
              >
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">UPLOADED FILES (Simulation)</h2>
                </div>

                <div className="p-6">
                  {uploadedFiles.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {uploadedFiles.map((file) => (
                        <FileItem key={file.id} file={file} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaFileImage
                        className={`mx-auto text-4xl mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      />
                      <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        No files uploaded yet. Use the upload area above to add files.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Debug */}
              <div className="mt-4 text-xs text-gray-500">
                API: {API_BASE}/folders?contractId={contractId}&branchId={branchId}&branchType=
                {encodeURIComponent(branchType || "")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoDocumentComponent;